import uniq from 'lodash/uniq';
import React, { Component, ComponentType, ErrorInfo, ReactNode } from 'react';
import { Typography } from "@mui/material";
import { captureException } from "../../vendor/sentry";

interface Props {
  children?: ReactNode;
  fallback?: ComponentType<State>;
}

export interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: undefined,
  };

  public static getDerivedStateFromError(): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureException(error, errorInfo);
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <this.props.fallback {...this.state} />;
      }
      return (
        <div id="error-boundary">
          <Typography variant="h1">Something went wrong.</Typography>
          <Typography>Please refresh the page and try again.</Typography>
          <code>{JSON.stringify(this.state.error)}</code>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

export enum HttpStatusCode {
  OK = 200,
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  Conflict = 409,
  UnprocessableEntity = 422,
  ServiceUnavailable = 503,
}

/**
 * Get a user-friendly error message when an unexpected error occurs.
 */
export const errorMessage = (taskDescription: string) => (
  `Uh oh, something went wrong while ${taskDescription}. Please try again or contact us.`
);

/**
 * Get a user-friendly representation of an error object returned from Rails. Falls back to
 * JSON.stringify when the object cannot be parsed.
 */
export const formatErrors = (errors: any) => {
  try {
    const friendlyErrors = Object.keys(errors).map((attribute: string) => {
      const friendlyName = attribute.split('.').pop();
      return `${friendlyName} ${errors[attribute]}`;
    });
    return uniq(friendlyErrors).join(', ');
  } catch (e) {
    console.error(e);
    return JSON.stringify(errors);
  }
};
